"use client"

import { Images } from "@/constants/images"
import { DefaultImage } from "@/lib/external-components"
import { ComponentConfig } from "@measured/puck"
import Image from "next/image"
import "swiper/css"
import "swiper/css/pagination"
import { Autoplay } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import { twMerge } from "tailwind-merge"
import { ADMIN_API_CONFIG } from "../../../../../../constants/api-configs"
import { CommonStylesProps } from "../../lib/commonCSSProps"
import { generateResponsiveCSS } from "../../lib/helper"
import useIsMobile from "../../lib/use-is-mobile"
import "./css/custom-bullet.css"

export interface AutoSliderProps extends CommonStylesProps {
  slides: {
    id?: number
    image?: string
    url: string
    description?: string
    name?: string
    position?: string
    avatar?: string
  }[]
  spaceBetween?: number
  slidesPerView?: number
  slidesPerViewMobile?: number
  spaceBetweenMobile?: number
  loop?: boolean
  speed?: number
  cardType?: "image" | "testimonial"
}

export const RenderConfig: ComponentConfig<AutoSliderProps> = {
  render: ({
    slides = [],
    spaceBetween,
    speed,
    slidesPerView,
    slidesPerViewMobile,
    spaceBetweenMobile,
    styles,
    loop,
    responsiveType,
    className,
    cardType = "testimonial",
  }) => {
    const id = `auto-slider-${Math.random().toString(36).substr(2, 9)}`

    const mergedStyles = {
      mobile: { ...styles?.mobile },
      tablet: { ...styles?.tablet },
      desktop: { ...styles?.desktop },
    }

    const responsiveCSS = generateResponsiveCSS(id, mergedStyles || {}, responsiveType)
    const isMobile = useIsMobile()

    const renderSlide = (slide: any, index: number) => {
      if (cardType === "image") {
        return (
          <Image
            src={slide.image ? `${ADMIN_API_CONFIG.API_URL}${slide.image}` : DefaultImage}
            alt={`Slide ${index + 1}`}
            width={120}
            height={120}
            className="rounded-lg object-contain w-full h-auto"
          />
        )
      }

      return (
        <div className="bg-[#F2F2F7] rounded-3xl justify-between p-8 flex flex-col gap-3 h-full">
          <div className="flex flex-col sm:gap-3 gap-2">
            <Image src={Images.quoteIcon} alt="quote" width={28} height={24} className="w-5 h-4" />
            {slide.description && (
              <p className="text-[#242F3E] sm:text-base sm:leading-[26px] text-sm leading-[22px]">
                {slide.description}
              </p>
            )}
          </div>

          <div className="flex items-center sm:gap-2 gap-1">
            <div className="sm:w-12 sm:h-12 w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
              <img
                src={slide.avatar ? `${ADMIN_API_CONFIG.API_URL}${slide.avatar}` : DefaultImage}
                alt={slide.name || "Avatar"}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="flex flex-col">
              {slide.name && (
                <p className="font-bold text-[#242F3E] sm:text-base sm:leading-[27px] text-sm leading-[22px]">
                  {slide.name}
                </p>
              )}
              {slide.position && (
                <p className="text-[#242F3E] sm:text-xs sm:leading-[18px] text-xs leading-[18px]">{slide.position}</p>
              )}
            </div>
          </div>
        </div>
      )
    }

    return (
      <>
        {responsiveCSS}
        <Swiper
          allowTouchMove={true}
          autoplay={{
            delay: 0,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
            reverseDirection: false,
          }}
          direction="horizontal"
          loop={loop}
          modules={[Autoplay]}
          slidesPerView={isMobile ? slidesPerViewMobile : slidesPerView}
          spaceBetween={isMobile ? spaceBetweenMobile : spaceBetween}
          speed={speed || 3000}
          id={id}
          className={`${twMerge(className, id)} swiper-transition`}
        >
          {slides
            .concat(slides)
            .concat(slides)
            .map((slide, index) => (
              <SwiperSlide key={index}>{renderSlide(slide, index)}</SwiperSlide>
            ))}
        </Swiper>
      </>
    )
  },
}
